import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, type Checklist as ChecklistData } from "../api";
import { ErrorNotice, Loading } from "../ui";
import { YearPicker } from "./Work";

const gap = (px: number) => ({ "--gap": `${px}px` }) as React.CSSProperties;
const fyOf = (date: string) => `FY${Number(date.slice(0, 4)) + (Number(date.slice(5, 7)) >= 7 ? 1 : 0)}`;
const STATUS: Record<ChecklistData["items"][number]["status"], { text: string; className: string }> = {
  have: { text: "✓ Have it", className: "pill ok" },
  missing: { text: "Missing", className: "pill warn" },
  not_yet: { text: "Not out yet", className: "pill" },
  not_needed: { text: "Not needed", className: "pill" },
};

export function Checklist() {
  const [params, setParams] = useSearchParams();
  const now = new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });
  const fy = params.get("fy") ?? `FY${Number(fyOf(now).slice(2)) - 1}`;
  const [data, setData] = useState<ChecklistData | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    setData(null);
    api.get<ChecklistData>(`/api/checklist/${fy}`).then(setData).catch(setError);
  }, [fy]);
  const mark = async (key: string, status: "received" | "not_needed" | "clear") => {
    try {
      setError(null);
      setData(await api.put<ChecklistData>(`/api/checklist/${fy}/${encodeURIComponent(key)}`, { status }));
    } catch (e) { setError(e); }
  };
  if (!data) return <><ErrorNotice error={error} /><Loading /></>;
  const groups = [...new Set(data.items.map((i) => i.about))];
  return (
    <div className="stack" style={gap(24)}>
      <div className="row wrap">
        <div>
          <h1>Documents for {data.label}</h1>
          <p className="muted">What your return needs, worked out from your jobs, properties, loans and cars. {data.total > 0 && <>You have <strong>{data.have} of {data.total}</strong>.</>}</p>
        </div>
        <div className="spacer" />
        <YearPicker fy={fy} onChange={(y) => setParams({ fy: y })} />
      </div>
      <ErrorNotice error={error} />
      {data.items.length === 0 && <div className="card flat empty">Nothing expected yet. Add your jobs, properties, loans and cars, and the documents each needs will appear here.</div>}
      {groups.map((about) => (
        <section key={about} className="stack" style={gap(8)}>
          <h2>{about}</h2>
          <div className="card list-card list">
            {data.items.filter((i) => i.about === about).map((i) => (
              <div key={i.key} className="list-item" style={{ alignItems: "flex-start" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{i.title}{i.usual && <span className="muted small"> · usually</span>}</div>
                  <div className="muted small">{i.evidence ?? i.where}</div>
                  {i.status === "missing" && (
                    <div className="row small" style={{ gap: 12, marginTop: 4 }}>
                      {i.link && <Link to={i.link}>Add it</Link>}
                      <button type="button" className="link" onClick={() => mark(i.key, "received")}>I have it elsewhere</button>
                      <button type="button" className="link" onClick={() => mark(i.key, "not_needed")}>Not needed this year</button>
                    </div>
                  )}
                  {(i.status === "not_needed" || i.evidence === "Ticked off by you") && <button type="button" className="link small" onClick={() => mark(i.key, "clear")}>Undo</button>}
                </div>
                <span className={STATUS[i.status].className}>{STATUS[i.status].text}</span>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
